import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Calendar, NotebookPen, Sparkles, Heart,
  ChevronDown, Check, Star
} from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[hsl(var(--bg-base))] text-[hsl(var(--text-primary))]">
      <Header />
      <Hero />
      <InteractivePreview />
      <FeatureGrid />
      <ForPatients />
      <Pricing />
      <Faq />
      <Cta />
      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="px-6 md:px-10 h-16 flex items-center justify-between border-b border-[hsl(var(--border-divider))] bg-[hsl(var(--bg-base))]/80 backdrop-blur sticky top-0 z-30">
      <Link to="/" className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-white overflow-hidden flex items-center justify-center shadow-sm p-1.5">
          <img src="/iatros-logo-icon-simple.svg" alt="Iatrós Logo" className="w-full h-full object-contain" />
        </div>
        <div className="leading-tight">
          <div className="text-[15px] font-bold tracking-tight text-[hsl(var(--brand))]" style={{ fontFamily: "'Outfit',sans-serif" }}>Iatrós</div>
          <div className="text-[11px] text-[hsl(var(--text-tertiary))] -mt-0.5 font-medium uppercase tracking-tighter">Cuidado clínico em um só lugar</div>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-1 text-sm">
        <a href="#features" className="px-3 py-2 rounded-md text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]">Recursos</a>
        <a href="#pricing" className="px-3 py-2 rounded-md text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]">Preços</a>
        <a href="#faq" className="px-3 py-2 rounded-md text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]">FAQ</a>
      </nav>
      <div className="flex items-center gap-2">
        <Link to="/login" data-testid="landing-login" className="ds-btn-ghost">Entrar</Link>
        <Link to="/cadastro/profissional" data-testid="landing-signup-pro" className="ds-btn-primary">
          Sou profissional <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="px-6 md:px-10 pt-12 md:pt-20 pb-12 max-w-6xl mx-auto relative overflow-hidden">
      {/* Background Watermark */}
      <div className="absolute -right-16 top-1/2 -translate-y-1/2 w-[480px] h-[480px] opacity-[0.07] select-none pointer-events-none hidden lg:block transform rotate-[15deg]">
        <img src="/iatros-logo-icon-simple.svg" alt="" className="w-full h-full object-contain" />
      </div>
      <div className="grid lg:grid-cols-2 gap-12 items-center relative z-10">
        <div className="ds-fade-up">
          <h1
            className="text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Outfit',sans-serif" }}
          >
            Prontuário e agenda{" "}
            <span className="text-[hsl(var(--brand))]">com a calma</span>{" "}
            que sua prática clínica merece.
          </h1>
          <p className="mt-5 text-lg text-[hsl(var(--text-secondary))] max-w-xl leading-relaxed">
            Gerencie pacientes, evoluções clínicas (modelo SOAP), anamneses e agendamentos em uma plataforma desenhada com cuidado para profissionais de saúde mental.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cadastro/profissional" data-testid="hero-signup-pro" className="ds-btn-primary h-12 px-6 text-base">
              Criar conta de profissional <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/cadastro/paciente" data-testid="hero-signup-patient" className="ds-btn-secondary h-12 px-6 text-base">
              Sou paciente
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-4 text-xs text-[hsl(var(--text-tertiary))]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[hsl(var(--brand))]" /> Dados sensíveis criptografados</span>
            <span>•</span>
            <span>Conforme LGPD</span>
          </div>
        </div>
        <div className="hidden lg:block ds-fade-up">
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl bg-[hsl(var(--brand-light))]/40 blur-2xl" />
            <div className="relative ds-clinical-card overflow-hidden">
              <div className="ds-overline">Hoje · 15:00</div>
              <h3 className="text-xl font-semibold mt-1" style={{ fontFamily: "'Outfit',sans-serif" }}>
                Ana Carolina · Sessão #14
              </h3>
              <p className="text-sm text-[hsl(var(--text-secondary))] mt-2">
                "Conseguiu fazer apresentação no trabalho. Boa colaboração na exposição gradual."
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                <span className="ds-badge ds-badge-brand">TCC</span>
                <span className="ds-badge ds-badge-info">Humor 7/10</span>
                <span className="ds-badge ds-badge-success">Adesão alta</span>
              </div>
              <div className="ds-divider my-4" />
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Stat label="Pacientes ativos" value="34" />
                <Stat label="Sessões este mês" value="98" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-[hsl(var(--bg-base))] border border-[hsl(var(--border-divider))] p-3">
      <div className="ds-overline">{label}</div>
      <div className="text-2xl font-semibold mt-0.5" style={{ fontFamily: "'Outfit',sans-serif" }}>{value}</div>
    </div>
  );
}

function InteractivePreview() {
  const [activeTab, setActiveTab] = useState("prontuario");

  return (
    <section className="px-6 md:px-10 py-16 max-w-6xl mx-auto border-t border-[hsl(var(--border-divider))]">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="ds-overline mb-2">Demonstração Interativa</div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit',sans-serif" }}>
          Veja como facilitamos seu dia a dia
        </h2>
        <p className="text-sm text-[hsl(var(--text-secondary))] mt-3">
          Explore as principais ferramentas do Iatrós clicando nos botões abaixo para ver uma demonstração real do sistema.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-8 items-center">
        {/* Tabs selector */}
        <div className="lg:col-span-2 space-y-3">
          {[
            { id: "prontuario", icon: NotebookPen, title: "Prontuário SOAP", desc: "Histórico clínico estruturado de forma prática e em conformidade técnica." },
            { id: "agenda", icon: Calendar, title: "Agenda Inteligente", desc: "Agendamentos reativos, lembretes de status e gerenciamento de ausências." },
            { id: "financeiro", icon: Sparkles, title: "Gestão Financeira", desc: "Controle de receitas de sessões, comissão e repasses automáticos." }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${activeTab === tab.id
                  ? "bg-white border-[hsl(var(--brand))] shadow-sm ring-1 ring-[hsl(var(--brand))]"
                  : "bg-transparent border-[hsl(var(--border-divider))] hover:border-[hsl(var(--text-tertiary))]"
                }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeTab === tab.id ? "bg-[hsl(var(--brand))] text-white" : "bg-[hsl(var(--brand-light))]/50 text-[hsl(var(--brand))]"
                  }`}>
                  <tab.icon className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[hsl(var(--text-primary))]">{tab.title}</h4>
                  <p className="text-xs text-[hsl(var(--text-secondary))] mt-0.5 leading-relaxed">{tab.desc}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Live Mockup */}
        <div className="lg:col-span-3">
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-[hsl(var(--brand-light))]/30 blur-xl" />
            <div className="relative bg-white border border-[hsl(var(--border-default))] rounded-2xl shadow-lg p-6 ds-fade-in min-h-[290px] flex flex-col justify-between">

              {activeTab === "prontuario" && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <span className="ds-overline">Prontuário · SOAP</span>
                    <span className="ds-badge ds-badge-brand">Paciente Ativo</span>
                  </div>
                  <h4 className="text-lg font-semibold text-[hsl(var(--brand))]" style={{ fontFamily: "'Outfit',sans-serif" }}>Ana Carolina Silva</h4>
                  <div className="mt-4 space-y-3 flex-1">
                    <div className="bg-[hsl(var(--bg-base))] p-3 rounded-lg border border-[hsl(var(--border-divider))]">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--brand))]">Subjetivo (S)</span>
                      <p className="text-xs text-[hsl(var(--text-secondary))] mt-1">"Relatou melhora nos sintomas de ansiedade ao falar em público após exposição gradual."</p>
                    </div>
                    <div className="bg-[hsl(var(--bg-base))] p-3 rounded-lg border border-[hsl(var(--border-divider))]">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#93A889]">Objetivo (O)</span>
                      <p className="text-xs text-[hsl(var(--text-secondary))] mt-1">Contato visual sustentado por mais tempo e postura corporal notavelmente mais relaxada durante a sessão.</p>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "agenda" && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <span className="ds-overline">Agenda Semanal</span>
                    <span className="ds-badge ds-badge-success">Hoje</span>
                  </div>
                  <div className="space-y-3 flex-1 flex flex-col justify-center">
                    <div className="flex justify-between items-center bg-[hsl(var(--bg-base))] p-3 rounded-lg border-l-4 border-emerald-500 shadow-xs">
                      <div>
                        <div className="text-xs font-semibold text-[hsl(var(--text-primary))]">09:00 · Dr. Roberto Oliveira</div>
                        <div className="text-[10px] text-[hsl(var(--text-secondary))]">Paciente: Mateus Santos</div>
                      </div>
                      <span className="ds-badge ds-badge-success text-[10px]">Confirmado</span>
                    </div>
                    <div className="flex justify-between items-center bg-[hsl(var(--bg-base))] p-3 rounded-lg border-l-4 border-amber-500 shadow-xs">
                      <div>
                        <div className="text-xs font-semibold text-[hsl(var(--text-primary))]">10:30 · Dra. Aline Mendes</div>
                        <div className="text-[10px] text-[hsl(var(--text-secondary))]">Paciente: Amanda Vieira</div>
                      </div>
                      <span className="ds-badge ds-badge-warning text-[10px]">Falta (Isenta)</span>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "financeiro" && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <span className="ds-overline">Painel de Faturamento</span>
                    <span className="text-[10px] text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-full">Reconciliado</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-[hsl(var(--bg-base))] p-3 rounded-lg border border-[hsl(var(--border-divider))]">
                      <span className="ds-overline">Faturamento Bruto</span>
                      <div className="text-lg font-bold text-[hsl(var(--brand))]" style={{ fontFamily: "'Outfit',sans-serif" }}>R$ 5.840,00</div>
                    </div>
                    <div className="bg-[hsl(var(--bg-base))] p-3 rounded-lg border border-[hsl(var(--border-divider))]">
                      <span className="ds-overline">Repasses Líquidos</span>
                      <div className="text-lg font-bold text-[#93A889]" style={{ fontFamily: "'Outfit',sans-serif" }}>R$ 4.120,00</div>
                    </div>
                  </div>
                  <div className="space-y-1.5 flex-1 flex flex-col justify-end">
                    <div className="flex justify-between text-xs text-[hsl(var(--text-secondary))]">
                      <span>Taxas Pix (Gateway)</span>
                      <span className="font-semibold text-[hsl(var(--text-primary))]">R$ 41,20</span>
                    </div>
                    <div className="flex justify-between text-xs text-[hsl(var(--text-secondary))]">
                      <span>Comissão da Clínica (20%)</span>
                      <span className="font-semibold text-[hsl(var(--text-primary))]">R$ 1.168,00</span>
                    </div>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureGrid() {
  const items = [
    { icon: NotebookPen, title: "Prontuário SOAP", body: "Anamnese estruturada e evoluções com Subjetivo/Objetivo/Avaliação/Plano em uma timeline limpa." },
    { icon: Calendar, title: "Agenda integrada", body: "Visualização semanal, modalidade presencial ou online, status de confirmação e lembretes." },
    { icon: ShieldCheck, title: "LGPD by design", body: "Permissões por linha (Row-Level Security), separação rigorosa profissional ↔ paciente." },
    { icon: Sparkles, title: "Convites por código", body: "Vincule pacientes existentes em segundos via código único enviado por você." },
  ];
  return (
    <section id="features" className="px-6 md:px-10 py-16 max-w-6xl mx-auto border-t border-[hsl(var(--border-divider))]">
      <div className="ds-overline">Recursos</div>
      <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2" style={{ fontFamily: "'Outfit',sans-serif" }}>
        Construído por quem entende o consultório.
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-8 ds-stagger">
        {items.map((it) => (
          <div key={it.title} className="ds-card ds-card-hover p-6">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))] flex items-center justify-center">
              <it.icon className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-semibold mt-4 tracking-tight" style={{ fontFamily: "'Outfit',sans-serif" }}>{it.title}</h3>
            <p className="text-sm text-[hsl(var(--text-secondary))] mt-2 leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ForPatients() {
  return (
    <section id="patients" className="px-6 md:px-10 py-16 max-w-6xl mx-auto border-t border-[hsl(var(--border-divider))]">
      <div className="ds-clinical-card">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <div className="ds-overline">Para pacientes</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2 max-w-2xl" style={{ fontFamily: "'Outfit',sans-serif" }}>
              Cuidado contínuo na palma da mão.
            </h2>
            <p className="text-[hsl(var(--text-secondary))] mt-3 max-w-2xl leading-relaxed">
              Vincule-se ao seu psicólogo com um código de convite ou escolhendo da lista. Acompanhe agendamentos, suas sessões e mantenha tudo organizado em um só lugar.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/cadastro/paciente" data-testid="patients-signup" className="ds-btn-primary">
                <Heart className="w-4 h-4" /> Criar minha conta
              </Link>
              <Link to="/login" className="ds-btn-secondary">Já tenho conta</Link>
            </div>
          </div>
          <div className="space-y-3">
            <Step n="1" title="Crie sua conta" desc="Em menos de 1 minuto." />
            <Step n="2" title="Vincule-se ao profissional" desc="Use código de convite ou escolha da lista." />
            <Step n="3" title="Pronto" desc="Acompanhe sessões e agenda." />
          </div>
        </div>
      </div>
    </section>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand))] text-white flex items-center justify-center text-sm font-semibold shrink-0">{n}</div>
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-[hsl(var(--text-tertiary))]">{desc}</div>
      </div>
    </div>
  );
}


function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: "Solo",
      desc: "Perfeito para profissionais autônomos iniciando.",
      monthlyPrice: 49.90,
      annualPrice: 41.90,
      features: [
        "Pacientes ilimitados",
        "Prontuário SOAP estruturado",
        "Agenda integrada reativa",
        "Portal do paciente completo",
        "Atendimento conforme LGPD"
      ]
    },
    {
      name: "Clínica Essential",
      desc: "Para clínicas e equipes em pleno crescimento.",
      monthlyPrice: 89.90,
      annualPrice: 74.90,
      popular: true,
      features: [
        "Tudo do plano Solo",
        "Até 5 profissionais integrados",
        "Painel de repasse dinâmico",
        "Acesso para secretária",
        "Visualização de faturamento unificada",
        "Suporte preferencial por WhatsApp"
      ]
    },
    {
      name: "Clínica Pro",
      desc: "Gestão total de grandes equipes e multifuncionalidades.",
      monthlyPrice: 149.90,
      annualPrice: 124.90,
      features: [
        "Tudo do plano Essential",
        "Membros de equipe ilimitados",
        "Multi-clínicas/Sedes simultâneas",
        "Reconciliação retroativa absoluta",
        "Gerente de conta exclusivo"
      ]
    }
  ];

  return (
    <section id="pricing" className="px-6 md:px-10 py-16 max-w-6xl mx-auto">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="ds-overline mb-2">Planos & Preços</div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit',sans-serif" }}>
          Um plano para cada etapa de crescimento
        </h2>
        <p className="text-sm text-[hsl(var(--text-secondary))] mt-3">
          Escolha a opção ideal para a sua prática clínica. Sem pegadinhas, sem taxas surpresa.
        </p>

        {/* Toggle Billing */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <span className={`text-sm ${!isAnnual ? "font-semibold text-[hsl(var(--text-primary))]" : "text-[hsl(var(--text-secondary))]"}`}>Mensal</span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="w-12 h-6 rounded-full bg-[hsl(var(--brand-light))] p-1 transition-colors relative"
            aria-label="Alternar faturamento"
          >
            <div className={`w-4 h-4 rounded-full bg-[hsl(var(--brand))] transition-transform ${isAnnual ? "translate-x-6" : ""}`} />
          </button>
          <span className={`text-sm flex items-center gap-1.5 ${isAnnual ? "font-semibold text-[hsl(var(--text-primary))]" : "text-[hsl(var(--text-secondary))]"}`}>
            Anual <span className="text-[10px] font-bold bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Economize 16%</span>
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-12 items-stretch">
        {plans.map((plan) => {
          const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
          return (
            <div key={plan.name} className={`bg-white border rounded-3xl p-8 flex flex-col justify-between transition-all duration-200 relative ${plan.popular
                ? "border-2 border-[hsl(var(--brand))] shadow-md scale-[1.02] md:scale-[1.03]"
                : "border-[hsl(var(--border-default))] shadow-xs hover:border-[hsl(var(--text-tertiary))]"
              }`}>
              {plan.popular && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[hsl(var(--brand))] text-white text-[10px] font-bold uppercase tracking-wider px-3.5 py-1 rounded-full shadow-sm">
                  Mais Popular
                </div>
              )}

              <div>
                <h3 className="text-xl font-bold text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit',sans-serif" }}>{plan.name}</h3>
                <p className="text-xs text-[hsl(var(--text-secondary))] mt-2 min-h-[32px] leading-relaxed">{plan.desc}</p>

                <div className="my-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs font-semibold text-[hsl(var(--text-secondary))]">R$</span>
                    <span className="text-4xl font-extrabold text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit',sans-serif" }}>
                      {price.toFixed(2).replace(".", ",")}
                    </span>
                    <span className="text-xs text-[hsl(var(--text-tertiary))]">/mês</span>
                  </div>
                  {isAnnual && (
                    <div className="text-[10px] text-emerald-600 font-semibold mt-1">Cobrado anualmente (R$ {(price * 12).toFixed(2).replace(".", ",")})</div>
                  )}
                </div>

                <div className="ds-divider my-6" />

                <ul className="space-y-3">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-xs text-[hsl(var(--text-secondary))]">
                      <Check className="w-4 h-4 text-[hsl(var(--brand))] shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8">
                <Link
                  to="/cadastro/profissional"
                  className={`w-full text-center block py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${plan.popular
                      ? "bg-[hsl(var(--brand))] text-white hover:bg-[hsl(var(--brand-hover))] shadow-sm"
                      : "bg-[hsl(var(--bg-base))] text-[hsl(var(--brand))] border border-[hsl(var(--border-default))] hover:border-[hsl(var(--text-tertiary))]"
                    }`}
                >
                  Começar agora
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Faq() {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "Como funciona o período de teste gratuito?",
      a: "Você pode se cadastrar gratuitamente como profissional e testar todas as funcionalidades (agenda, prontuário, finanças) sem compromisso por 14 dias. Não é necessário cartão de crédito."
    },
    {
      q: "Meus dados de pacientes estão realmente seguros?",
      a: "Sim, absolutamente. Todas as evoluções clínicas, prontuários e dados sensíveis são protegidos por criptografia de ponta a ponta e armazenados de forma isolada, em total conformidade com a LGPD (Lei Geral de Proteção de Dados) e sob as normas do CFP/CRM."
    },
    {
      q: "O portal do paciente tem algum custo adicional?",
      a: "Não. O portal do paciente é 100% gratuito e já está incluso em todos os planos. Seus pacientes podem consultar horários, confirmar sessões e visualizar recibos sem pagar nada por isso."
    },
    {
      q: "Consigo fazer a gestão de repasse de profissionais parceiros?",
      a: "Sim! No plano Clínica (Essential ou Pro), você gerencia múltiplos profissionais, define comissões e calcula os repasses financeiros de forma totalmente automatizada e em tempo real."
    }
  ];

  return (
    <section id="faq" className="px-6 md:px-10 py-16 max-w-4xl mx-auto border-t border-[hsl(var(--border-divider))]">
      <div className="text-center mb-10">
        <div className="ds-overline mb-2">Dúvidas Frequentes</div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit',sans-serif" }}>
          Perguntas Frequentes
        </h2>
        <p className="text-sm text-[hsl(var(--text-secondary))] mt-3">
          Tire suas principais dúvidas sobre o Iatrós de forma rápida.
        </p>
      </div>

      <div className="space-y-4 mt-8">
        {faqs.map((faq, idx) => {
          const isOpen = openFaq === idx;
          return (
            <div key={idx} className="bg-white border border-[hsl(var(--border-default))] rounded-2xl overflow-hidden shadow-xs hover:border-[hsl(var(--text-tertiary))] transition-colors duration-200">
              <button
                onClick={() => setOpenFaq(isOpen ? null : idx)}
                className="w-full px-6 py-4 flex items-center justify-between text-left gap-4"
              >
                <span className="text-sm font-semibold text-[hsl(var(--text-primary))]">{faq.q}</span>
                <ChevronDown className={`w-4 h-4 text-[hsl(var(--text-secondary))] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`transition-all duration-300 ease-in-out ${isOpen ? "max-h-[200px] border-t border-[hsl(var(--border-divider))]" : "max-h-0"
                } overflow-hidden`}>
                <p className="px-6 py-4 text-xs text-[hsl(var(--text-secondary))] leading-relaxed bg-[hsl(var(--bg-base))]/40">
                  {faq.a}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Cta() {
  return (
    <section className="px-6 md:px-10 py-16">
      <div className="max-w-4xl mx-auto rounded-3xl bg-[hsl(var(--brand))] text-white p-10 md:p-14 text-center relative overflow-hidden">
        {/* Glowing Watermark */}
        <div className="absolute right-[-10%] top-[-25%] w-[320px] h-[320px] opacity-[0.08] pointer-events-none select-none transform rotate-[8deg]">
          <img src="/iatros-logo-icon-simple-dark.svg" alt="" className="w-full h-full object-contain" />
        </div>
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: "'Outfit',sans-serif" }}>
          Comece com seus pacientes hoje.
        </h2>
        <p className="text-white/80 mt-3 max-w-xl mx-auto">
          Cadastre-se gratuitamente como profissional e convide seus pacientes em poucos cliques.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/cadastro/profissional" className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-white text-[hsl(var(--brand))] font-medium hover:bg-white/90 transition-colors">
            Criar conta de profissional <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-6 md:px-10 py-10 border-t border-[hsl(var(--border-divider))]">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-sm text-[hsl(var(--text-tertiary))]">
        <div>© {new Date().getFullYear()} Iatrós · Gestão Clínica Inteligente</div>
        <div className="flex items-center gap-5">
          <Link to="/legal/termos" className="hover:text-[hsl(var(--text-primary))]">Termos de Uso</Link>
          <Link to="/legal/privacidade" className="hover:text-[hsl(var(--text-primary))]">Política de Privacidade</Link>
        </div>
      </div>
    </footer>
  );
}
